import { fileUrl } from "@/lib/api";
import { NUM, TRY, unitLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ArrowDown, ArrowUp, ArrowUpDown, Package, PanelRightOpen, Loader2 } from "lucide-react";
import { EditableCell } from "./EditableCell";
import { SplitSlider } from "./SplitSlider";

const COLS = [
  ["", "", "w-10"], ["name", "Ürün", "min-w-[260px]"], ["brand", "Marka", "min-w-[110px]"], ["series", "Seri", "min-w-[120px]"], ["category", "Kategori", "min-w-[110px]"],
  ["", "Birim", "min-w-[70px]"], ["depo", "Depo", "min-w-[84px] text-right"], ["magaza", "Mağaza", "min-w-[84px] text-right"],
  ["", "Dağılım", "min-w-[150px]"], ["quantity", "Toplam", "min-w-[90px] text-right"], ["critical", "Kritik", "min-w-[70px] text-right"],
  ["list_price", "Liste Fiyatı", "min-w-[110px] text-right"], ["", "Döviz", "min-w-[64px]"], ["", "İsk. Grubu", "min-w-[110px]"],
  ["cost", "Maliyet", "min-w-[100px] text-right"], ["value", "Stok Değeri", "min-w-[110px] text-right"], ["", "", "w-12"],
];
const STICKY = "sticky z-10 bg-white group-hover:bg-slate-50";
export const thumbOf = (p) => (p.images && p.images[0]) || p.image_url;

export function Thumb({ p, size = "h-9 w-9" }) {
  const img = thumbOf(p);
  return img
    ? <img src={fileUrl(img)} alt="" loading="lazy" className={cn(size, "rounded-md object-contain bg-white border border-slate-200 shrink-0")} />
    : <div className={cn(size, "rounded-md bg-slate-100 flex items-center justify-center shrink-0")}><Package className="h-4 w-4 text-slate-300" /></div>;
}

function SortHead({ k, label, cls, sort, onSort, first }) {
  const on = k && sort.key === k;
  const Icon = !on ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={cn("py-2.5 px-3 text-[11px] font-semibold text-slate-600 uppercase tracking-wider bg-slate-50 border-b border-slate-200 sticky top-0 z-20 whitespace-nowrap", cls, first && "left-10 z-30")}>
      {k ? (
        <button type="button" onClick={() => onSort(k)} className={cn("inline-flex items-center gap-1 hover:text-[#0F2745] transition-colors", on && "text-[#0F2745]")} data-testid={`sort-col-${k}`}>
          {label}<Icon className={cn("h-3 w-3", !on && "opacity-40")} />
        </button>
      ) : label}
    </th>
  );
}

function Row({ p, sel, onToggle, h }) {
  const save = (field) => (v) => h.saveField(p, field, v);
  const tl = p.currency && p.currency !== "TL";
  const crit = p.critical_level > 0 && p.quantity <= p.critical_level;
  return (
    <tr className={cn("border-b border-slate-100 hover:bg-slate-50 transition-colors group", sel && "bg-amber-50/60")} data-testid={`stock-row-${p.id}`}>
      <td className={cn(STICKY, "left-0 px-3 py-2", sel && "bg-amber-50")}><Checkbox checked={sel} onCheckedChange={() => onToggle(p.id)} data-testid={`checkbox-select-${p.id}`} /></td>
      <td className={cn(STICKY, "left-10 px-2 py-1.5 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.06)] max-w-[340px]", sel && "bg-amber-50")}>
        <div className="flex items-start gap-2">
          <button type="button" onClick={() => h.openDrawer(p)} className="mt-0.5" title="Ayrıntılar"><Thumb p={p} /></button>
          <div className="min-w-0 flex-1">
            <EditableCell value={p.name} onSave={save("name")} testid={`cell-edit-name-${p.id}`} className="font-semibold text-[13px] text-slate-800 leading-snug" />
            <EditableCell value={p.code} onSave={save("code")} testid={`cell-edit-code-${p.id}`} className="text-[11px] font-mono text-slate-500 min-h-[20px] py-0" placeholder="kod ekle" />
          </div>
        </div>
      </td>
      <td className="px-2 py-1.5 text-[13px]"><EditableCell value={p.brand} onSave={save("brand")} testid={`cell-edit-brand-${p.id}`} /></td>
      <td className="px-2 py-1.5 text-[13px]"><EditableCell value={p.series} onSave={save("series")} testid={`cell-edit-series-${p.id}`} placeholder="—" /></td>
      <td className="px-2 py-1.5 text-[13px]"><EditableCell value={p.category} onSave={save("category")} testid={`cell-edit-category-${p.id}`} /></td>
      <td className="px-2 py-1.5 text-[12px]"><EditableCell value={p.unit} display={unitLabel(p.unit)} onSave={save("unit")} testid={`cell-edit-unit-${p.id}`} /></td>
      <td className="px-2 py-1.5 text-[13px] bg-slate-50/60"><EditableCell type="number" align="right" value={p.qty_depo} display={NUM(p.qty_depo)} onSave={(v) => h.saveLoc(p, "depo", v)} testid={`cell-edit-depo-${p.id}`} className={cn("font-bold", p.qty_depo < 0 ? "text-red-600" : "text-slate-700")} /></td>
      <td className="px-2 py-1.5 text-[13px] bg-teal-50/50"><EditableCell type="number" align="right" value={p.qty_magaza} display={NUM(p.qty_magaza)} onSave={(v) => h.saveLoc(p, "magaza", v)} testid={`cell-edit-magaza-${p.id}`} className={cn("font-bold", p.qty_magaza < 0 ? "text-red-600" : "text-teal-700")} /></td>
      <td className="px-3 py-1.5"><SplitSlider depo={p.qty_depo} magaza={p.qty_magaza} compact onCommit={({ to, qty }) => h.move(p, to, qty)} testid={`slider-split-${p.id}`} /></td>
      <td className={cn("px-3 py-1.5 text-right tnum text-[13px] font-bold whitespace-nowrap", p.quantity < 0 ? "text-red-600" : crit ? "text-amber-600" : "text-slate-800")} data-testid={`cell-total-${p.id}`}>{NUM(p.quantity)}</td>
      <td className="px-2 py-1.5 text-[13px]"><EditableCell type="number" align="right" value={p.critical_level} display={NUM(p.critical_level)} onSave={save("critical_level")} testid={`cell-edit-critical-${p.id}`} /></td>
      <td className="px-2 py-1.5 text-[13px]">
        <EditableCell type="number" align="right" value={p.list_price} display={NUM(p.list_price)} onSave={save("list_price")} testid={`cell-edit-list_price-${p.id}`} />
        {tl && <div className="text-[10px] text-slate-400 text-right tnum px-1.5">{TRY(p.price)}</div>}
      </td>
      <td className="px-2 py-1.5 text-[12px]"><EditableCell value={p.currency || "TL"} options={["TL", "USD", "EUR"]} onSave={save("currency")} testid={`cell-edit-currency-${p.id}`} /></td>
      <td className="px-2 py-1.5 text-[12px]"><EditableCell value={p.discount_group} onSave={save("discount_group")} testid={`cell-edit-discount_group-${p.id}`} /></td>
      <td className="px-3 py-1.5 text-right tnum text-[13px] text-slate-600 whitespace-nowrap">{TRY(p.cost)}</td>
      <td className="px-3 py-1.5 text-right tnum text-[13px] font-semibold text-[#0F2745] whitespace-nowrap" data-testid={`cell-value-${p.id}`}>{TRY(p.value)}</td>
      <td className="px-2 py-1.5"><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => h.openDrawer(p)} data-testid={`btn-detail-${p.id}`}><PanelRightOpen className="h-4 w-4 text-slate-500" /></Button></td>
    </tr>
  );
}

/** Tablo görünümü — yapışkan başlık + ilk sütun, hücre bazlı anında düzenleme. */
export function PortalTable({ items, loading, sort, onSort, selected, onToggle, onTogglePage, h }) {
  const allOn = items.length > 0 && items.every((p) => selected.includes(p.id));
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="table-bulk-stock">
      <div className="max-h-[72vh] overflow-auto relative">
        <table className="w-full text-left border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="py-2.5 px-3 bg-slate-50 border-b border-slate-200 sticky top-0 left-0 z-30 w-10">
                <Checkbox checked={allOn} onCheckedChange={() => onTogglePage(!allOn)} data-testid="checkbox-select-all" />
              </th>
              {COLS.slice(1).map(([k, l, cls], i) => <SortHead key={i} k={k} label={l} cls={cls} sort={sort} onSort={onSort} first={i === 0} />)}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => <Row key={p.id} p={p} sel={selected.includes(p.id)} onToggle={onToggle} h={h} />)}
          </tbody>
        </table>
        {!loading && !items.length && <div className="py-16 text-center text-sm text-slate-500" data-testid="portal-empty">Filtreye uyan ürün yok</div>}
        {loading && <div className="absolute inset-0 bg-white/50 flex items-start justify-center pt-24"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
      </div>
    </div>
  );
}
